import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ExportButton from "components/button/ExportButton";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { formatCurrency, formatWheelRimDiamDisplay } from "utils";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { EstQuoteDetailTire, PrcFeesData } from "features/car_model/types";
import Notify from "config/notify";
import useLoadMore from "hooks/useLoadMore";
import FilterProcFeeModal from "features/mst_proc_fee/components/FilterProcFeeModal";
import TireListTable from "features/mst_proc_fee/components/TireListTable";
import useWheelTireProcFeeList from "features/mst_proc_fee/hooks/useWheelTireProcFeeList";
import ServiceCostSummary from "features/mst_proc_fee/components/ServiceCostSummary";
import { CostPriceMap, CostSettingInfo } from "features/mst_proc_fee/types/proc-fee";
import { IInfoCell } from "features/mst_store_tire_wheel_filter/types/wheel";
import {
  CASHBACK_PERIOD_OPTIONS,
  PUNCTURE_COMPENSATION_WHEEL_OPTIONS,
} from "features/mst_proc_fee/constants/options";
import { parseNumber } from "util/helper";
import { useTirePriceCalculatorWithCostSetting } from "features/mst_proc_fee/hooks/useTirePriceCalculator";
import { fetchCostPricesForTires } from "features/mst_proc_fee/api/procFeeApi";

const StyledLabelHead = styled("div")({
  backgroundColor: "var(--primary-500)",
  textAlign: "center",
  padding: "10px 0",
  fontSize: "20px",
  fontWeight: "bold",
});

/**
 * Main component for WheelTireProcFeeList
 */
function WheelTireProcFeeList() {
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stockFilterStore, setStockFilterStore] = useState(false);
  const [stockFilterDepotVendor, setStockFilterDepotVendor] = useState(false);

  const {
    costSettingInfo,
    setCostSettingInfo,
    makerList,
    categoryList,
    selectedMaker,
    setSelectedMaker,
    recommendFlag,
    setRecommendFlag,
    handleFilter,
    selectedWheel: selectedWheelDetail,
    selectedCar,
    tireSize,
    tireList,
    punctureProtection,
    selectedTireIds,
    setTotalSelectedTire,
    totalSelectedTire,
    setSelectedTireIds,
    setTireList,
    onLoadMore,
    loading,
    selectedCategory,
    setSelectedCategory,
    page,
    priceSettingWheel,
  } = useWheelTireProcFeeList();

  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const navigate = useNavigate();

  // Cost Price Map for special pricing
  const [costPriceMap, setCostPriceMap] = useState<CostPriceMap>({});

  // Fetch cost prices only when the response-level price_setting_wheel flag is 1.
  useEffect(() => {
    const loadCostPrices = async () => {
      const store_cd = localStorage.getItem("shop_cd");
      if (priceSettingWheel === 1 && tireList.length > 0 && store_cd) {
        const map = await fetchCostPricesForTires(tireList, store_cd);
        setCostPriceMap(map);
      } else {
        setCostPriceMap({});
      }
    };

    loadCostPrices();
  }, [tireList.length, priceSettingWheel]);

  // Use centralized calculator
  const { calculator } = useTirePriceCalculatorWithCostSetting({
    tireNumber: 4,
    costSettingInfo,
    punctureProtection,
    costPriceMap,
  });

  /** Φ beside wheel size — hub from session (`SELECTED_WHEEL` → `selectedWheelDetail.wheel_hub_diam`), not SKU `wheel_hub_diam`. */
  const hubRimDisplay = useMemo(
    () => formatWheelRimDiamDisplay(selectedWheelDetail.wheel_hub_diam),
    [selectedWheelDetail.wheel_hub_diam],
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFilterClick = () => {
    setIsModalOpen(true);
  };

  const navigateConfirmResult = () => {
    if (Object.values(selectedTireIds).every((item) => item === false)) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
      });
      return;
    }
    if (Object.values(errors).some((item) => item)) return;

    const sessionData: Record<string, any> = {};
    if (costSettingInfo) {
      sessionData.proc_fees = Object.keys(costSettingInfo).reduce((acc, key) => {
        switch (key) {
          case "replac_labor_cost":
            acc[key] = {
              value: parseNumber(costSettingInfo[key]),
              checked: true,
            };
            break;
          case "waste_tire_cost":
            acc[key] = {
              value: parseNumber(costSettingInfo[key]),
              checked: costSettingInfo.waste_tire_cost_flag,
            };
            break;
          case "rubber_stem_valve_cost":
            acc[key] = {
              value: parseNumber(costSettingInfo[key]),
              checked: costSettingInfo.rubber_stem_valve_flag,
            };
            break;
          case "nitrogen_inflation_cost":
            acc[key] = {
              value: parseNumber(costSettingInfo[key]),
              checked: costSettingInfo.nitrogen_inflation_flag,
            };
            break;
          case "hub_rust_prevention_cost": {
            acc[key] = {
              value: parseNumber(costSettingInfo[key]),
              checked: costSettingInfo.hub_rust_prevention_flag,
            };
            break;
          }
          default:
            break;
        }
        return acc;
      }, {} as PrcFeesData);
    }
    const detail: Partial<EstQuoteDetailTire>[] = [];
    tireList.forEach((tire) => {
      if (!selectedTireIds[String(tire.id)]) return;
      const result = calculator(tire);
      const {
        basePrice,
        discountedPrice,
        wage,
        totalPrice,
        compensation,
        totalPriceWithCompensation,
      } = result;
      detail.push({
        checked: true,
        tire_item_cd: tire.item_cd,
        maker_name: tire.maker_name,
        item_name: tire.item_name,
        tire_loin_speed_mark: tire.tire_loin_speed_mark,
        tire_label: tire.tire_label,
        quantity: 4,
        nomal_price: basePrice,
        fixed_discount: Number(tire.fixed_discount),
        special_discount: Number(tire.special_discount),
        discounted_sale_price: discountedPrice,
        wage_cost_price: wage,
        wage_cost_in_price: totalPrice,
        puncture_protection_price: compensation,
        puncture_protection_in_price: totalPriceWithCompensation,
        cb_tire_price: tire.cb_tire_price,
        cb_wheel_price: tire.cb_wheel_price,
        confirm_total_price: totalPriceWithCompensation,
        remarks: "",
      });
    });
    sessionData.tbl_quote_detail_tire = detail;
    sessionData.cashback = {
      puncture: Number(costSettingInfo.tire_puncture_flag),
      cashback_period: Number(costSettingInfo.cashback_period),
    };

    // Save to SELECTED_TIRE for confirm screen
    sessionStorage.setItem(SESSION_KEY.SELECTED_TIRE, JSON.stringify(sessionData));
    sessionStorage.setItem(SESSION_KEY.SET_TIRE_RECOMMEND_FLAG, JSON.stringify(recommendFlag));
    sessionStorage.setItem(SESSION_KEY.SET_TIRE_CURRENT_PAGE, String(page));

    // Also save to MST_PROC_WHEEL_FEE for back navigation to this screen
    sessionStorage.setItem(SESSION_KEY.MST_PROC_WHEEL_FEE, JSON.stringify(sessionData));

    // Mark this as wheel flow (only 2 puncture options instead of 3)
    sessionStorage.setItem(SESSION_KEY.TIRE_WHEEL_TIRE_FROM, routes.mstWheelTireProcFee);

    // Update SELECTED_WHEEL to include current tire-related cost settings
    // This ensures confirm screen gets the latest values and doesn't use stale data
    try {
      const selectedWheelRaw = sessionStorage.getItem(SESSION_KEY.SELECTED_WHEEL);
      if (selectedWheelRaw) {
        const selectedWheel = JSON.parse(selectedWheelRaw);
        const updatedSelectedWheel = {
          ...selectedWheel,
          cost_setting: {
            ...selectedWheel.cost_setting,
            // Update tire-related fields with current values
            replac_labor_cost: costSettingInfo.replac_labor_cost,
            waste_tire_cost_flag: costSettingInfo.waste_tire_cost_flag,
            waste_tire_cost: costSettingInfo.waste_tire_cost,
            rubber_stem_valve_flag: costSettingInfo.rubber_stem_valve_flag,
            rubber_stem_valve_cost: costSettingInfo.rubber_stem_valve_cost,
            nitrogen_inflation_flag: costSettingInfo.nitrogen_inflation_flag,
            nitrogen_inflation_cost: costSettingInfo.nitrogen_inflation_cost,
            hub_rust_prevention_flag: costSettingInfo.hub_rust_prevention_flag,
            hub_rust_prevention_cost: costSettingInfo.hub_rust_prevention_cost,
            tire_puncture_flag: costSettingInfo.tire_puncture_flag,
            cashback_flag: costSettingInfo.cashback_flag,
            cashback_period: costSettingInfo.cashback_period,
          },
        };
        sessionStorage.setItem(SESSION_KEY.SELECTED_WHEEL, JSON.stringify(updatedSelectedWheel));
      }
    } catch (error) {
      // Silently handle error
    }

    navigate(routes.mstConfirmProcFee);
  };

  const onSelectTire = (event: React.ChangeEvent<HTMLInputElement>, tireId: string) => {
    if (totalSelectedTire >= 5 && event.target.checked) {
      Notify.setNotification({
        show: true,
        title: "5個までしか選択できません",
        actionsPosition: "end",
      });
      return;
    }
    if (event.target.checked) {
      setTotalSelectedTire((prev) => prev + 1);
    } else {
      setTotalSelectedTire((prev) => prev - 1);
    }
    setSelectedTireIds((prev) => ({
      ...prev,
      [tireId]: event.target.checked,
    }));
  };

  const costItems: IInfoCell[] = useMemo(
    () => [
      {
        sxBox: {
          width: {
            xs: "calc((100% - 2px)/2)",
            md: "calc((100% - 8px)/ 5)",
            xl: "calc((100% - 80px) *2 / 5)",
          },
        },
        label: "交換工賃",
        labelType: "text",
        sxLabel: {
          backgroundColor: "var(--green-600)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.replac_labor_cost,
        valueType: "input",
        sxValue: {
          textAlign: "center",
        },
        fieldName: "replac_labor_cost",
      },
      {
        sxBox: {
          width: {
            xs: "calc((100% - 2px)/2)",
            md: "calc((100% - 8px)/ 5)",
            xl: "calc((100% - 80px) *2 / 5)",
          },
        },
        label: "処分",
        labelType: "checkbox",
        sxLabel: {
          backgroundColor: costSettingInfo.waste_tire_cost_flag
            ? "var(--green-600)"
            : "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.waste_tire_cost,
        valueType: "input",
        fieldName: "waste_tire_cost",
        fieldNameLabel: "waste_tire_cost_flag",
      },
      {
        sxBox: {
          width: {
            xs: "calc((100% - 4px)/3)",
            md: "calc((100% - 8px)/ 5)",
            xl: "calc((100% - 80px) *2 / 5)",
          },
        },
        label: "バルブ",
        labelType: "checkbox",
        sxLabel: {
          backgroundColor: costSettingInfo.rubber_stem_valve_flag
            ? "var(--green-600)"
            : "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.rubber_stem_valve_cost,
        valueType: "input",
        fieldName: "rubber_stem_valve_cost",
        fieldNameLabel: "rubber_stem_valve_flag",
      },
      {
        sxBox: {
          width: {
            xs: "calc((100% - 4px)/3)",
            md: "calc((100% - 8px)/ 5)",
            xl: "calc((100% - 80px) *2 / 5)",
          },
        },
        label: "窒素",
        labelType: "checkbox",
        sxLabel: {
          backgroundColor: costSettingInfo.nitrogen_inflation_flag
            ? "var(--green-600)"
            : "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.nitrogen_inflation_cost,
        valueType: "input",
        fieldName: "nitrogen_inflation_cost",
        fieldNameLabel: "nitrogen_inflation_flag",
      },
      {
        sxBox: {
          width: {
            xs: "calc((100% - 4px)/3)",
            md: "calc((100% - 8px)/ 5)",
            xl: "calc((100% - 80px) *2 / 5)",
          },
        },
        label: "ハブ防錆",
        labelType: "checkbox",
        sxLabel: {
          backgroundColor: costSettingInfo.hub_rust_prevention_flag
            ? "var(--green-600)"
            : "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.hub_rust_prevention_cost,
        valueType: "input",
        valueOnly: true,
        fieldName: "hub_rust_prevention_cost",
        fieldNameLabel: "hub_rust_prevention_flag",
      },
    ],
    [costSettingInfo],
  );

  const quotationSummaryItems: IInfoCell[] = useMemo(
    () => [
      {
        sxBox: {
          width: { xs: "calc((100% - 2px)*3/5)" },
        },
        label: "パンク補償",
        labelType: "text",
        sxLabel: {
          backgroundColor: "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.tire_puncture_flag,
        valueType: "radio",
        sxValue: {
          textAlign: "center",
        },
        options: PUNCTURE_COMPENSATION_WHEEL_OPTIONS,
        fieldName: "tire_puncture_flag",
      },
      {
        sxBox: {
          width: { xs: "calc((100% - 2px)*2/5)" },
        },
        label: "キャッシュバック",
        labelType: "text",
        sxLabel: {
          backgroundColor: "var(--offwhite-500)",
          whiteSpace: "nowrap",
        },
        value: costSettingInfo.cashback_period,
        valueType: "radio",
        sxValue: {
          textAlign: "center",
        },
        options: CASHBACK_PERIOD_OPTIONS,
        fieldName: "cashback_period",
      },
    ],
    [costSettingInfo.tire_puncture_flag, costSettingInfo.cashback_period],
  );

  /** 店舗 在庫 と 倉庫/仕入先 在庫 の OR フィルタ。両方 false のときは無効。 */
  const filteredTireList = useMemo(() => {
    if (!stockFilterStore && !stockFilterDepotVendor) return tireList;
    return tireList.filter((tire) => {
      const storeMatch = stockFilterStore && (tire.store_quantity ?? 0) > 0;
      const depotVendorMatch =
        stockFilterDepotVendor &&
        ((tire.depot_quantity ?? 0) > 0 || (tire.vendor_centor_quantity ?? 0) > 0);
      return storeMatch || depotVendorMatch;
    });
  }, [tireList, stockFilterStore, stockFilterDepotVendor]);

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            columnGap: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: { xs: "100%", xl: "calc((100% - 80px) *3 / 5)" },
              gap: 3,
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: 200, md: 280 },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "28px", md: "33px" },
                  fontWeight: "medium",
                  color: "var(--gray-700)",
                }}
              >
                タイヤを選ぶ
              </Typography>

              <Box display="flex" flexDirection="column" width="100%" gap={2}>
                <img
                  src="/img/wheel/wheel.svg"
                  style={{ height: 150, width: "auto" }}
                  alt="車輪のイラスト"
                />
                <Typography
                  variant="h4"
                  sx={{ fontSize: { xs: "16px", md: "18px", xl: "20px" }, textAlign: "center" }}
                >
                  {selectedWheelDetail?.wheel_size} {hubRimDisplay}&Phi; <br />
                  <Typography sx={{ fontSize: 14 }}>
                    {selectedWheelDetail?.wheel_color_name}
                  </Typography>
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 4, flex: 1, flexDirection: "column" }}>
              <ExportButton
                label="ホイール選択へ戻る"
                sx={{ whiteSpace: "nowrap", width: "max-content" }}
                onClick={() => {
                  // Navigate back without clearing - preserve wheel selection and fasteners
                  navigate(routes.mstWheelStore);
                }}
              />
              <Box sx={{ display: "flex", columnGap: "2px", flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", md: "calc((100% - 4px) / 2)" } }}>
                  <StyledLabelHead>選択車両</StyledLabelHead>
                  <Box textAlign="center" margin="5px auto" minHeight={48} fontSize="20px">
                    {selectedCar?.car_model_and_period}
                  </Box>
                </Box>
                <Box sx={{ width: { xs: "100%", md: "calc((100% - 4px) / 2)" } }}>
                  <StyledLabelHead>タイヤサイズ</StyledLabelHead>
                  <Box textAlign="center" margin="5px auto" minHeight={48} fontSize="20px">
                    {`${tireSize?.tire_width}/${tireSize?.tire_aspect_ratio}R${tireSize?.tire_rim_diam}`}
                  </Box>
                </Box>
                <Box sx={{ width: { xs: "100%", md: "calc((100% - 4px) / 2)" } }}>
                  <StyledLabelHead>ホイール名称</StyledLabelHead>
                  <Box textAlign="center" margin="5px auto" minHeight={48} fontSize="20px">
                    {selectedWheelDetail?.item_name}
                  </Box>
                </Box>
                <Box sx={{ width: { xs: "100%", md: "calc((100% - 4px) / 2)" } }}>
                  <StyledLabelHead>選択ホイール価格</StyledLabelHead>
                  <Box textAlign="center" margin="5px auto" minHeight={48} fontSize="20px">
                    {formatCurrency(Number(selectedWheelDetail?.final_price))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexDirection: "column",
              width: { xs: "100%", xl: "calc((100% - 80px) *2 / 5)" },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: "medium",
                  fontSize: { xs: "28px", md: "33px" },
                  color: "var(--gray-700)",
                  textAlign: "right",
                  mb: 1.5,
                }}
              >
                工賃設定
              </Typography>
              <Typography
                sx={{
                  color: "var(--gray-700)",
                  textAlign: "right",
                  fontWeight: "600",
                  fontSize: { xs: 12, md: 14, lg: 16 },
                }}
              >
                1本あたりの税込金額
              </Typography>
            </Box>
            <ServiceCostSummary
              type="confirm"
              costItems={costItems}
              quotationSummaryItems={quotationSummaryItems}
              costSettingInfo={costSettingInfo as unknown as CostSettingInfo}
              setCostSettingInfo={setCostSettingInfo}
              sxBox={{ width: "100%" }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: "var(--offwhite-100)", p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: "20px",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "32px",
              alignItems: "center",
              rowGap: "20px",
            }}
          >
            <ExportButton
              label="選択したタイヤ＆ホイールセットを見積表へ"
              onClick={navigateConfirmResult}
              sx={{ whiteSpace: "nowrap", width: "max-content" }}
            />
            <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
              ※タイヤは最大5種類のみ選択できます
            </Typography>
          </Box>
          <ExportButton label="絞り込み" onClick={() => handleFilterClick()} />
        </Box>
        <TireListTable
          tireList={filteredTireList}
          tireNumber={4}
          selectedTireIds={selectedTireIds}
          cashbackPeriod={Number(costSettingInfo.cashback_period)}
          procFees={null}
          errors={errors}
          calculator={calculator}
          onSelectTire={onSelectTire}
          onTireNumberChange={() => {}} // Fixed at 4 for wheel variant
          onTireListChange={setTireList}
          onErrorChange={(field, error) => setErrors((prev) => ({ ...prev, [field]: error }))}
          variant="wheel"
        />
        {renderNodeLoader()}
      </Box>

      {isModalOpen && (
        <FilterProcFeeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          makerList={makerList}
          selectedMaker={selectedMaker}
          setSelectedMaker={setSelectedMaker}
          recommendFlag={recommendFlag}
          setRecommendFlag={setRecommendFlag}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          categoryList={categoryList}
          stockFilterStore={stockFilterStore}
          stockFilterDepotVendor={stockFilterDepotVendor}
          handleFilter={async (values) => {
            if (values) {
              setStockFilterStore(values.stockFilterStore ?? false);
              setStockFilterDepotVendor(values.stockFilterDepotVendor ?? false);
            }
            await handleFilter(values);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export default WheelTireProcFeeList;
